import { useState, useRef, useEffect, useCallback } from "react";
import { Video, Square, RotateCcw, Check } from "lucide-react";

const MAX_SECONDS = 120;

export const VideoRecorder = ({ questions, onRecorded, countdownSeconds = 3 }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const posterRef = useRef(null);
  const posterTimeoutRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | ready | countdown | recording | done
  const [error, setError] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => { stopStream(); clearInterval(timerRef.current); clearInterval(countdownRef.current); clearTimeout(posterTimeoutRef.current); }, []);

  const captureFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((b) => { posterRef.current = b; }, "image/jpeg", 0.8);
  };

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;
      setStatus("ready");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch {
      setError("Não foi possível acessar a câmera. Verifique as permissões do navegador.");
    }
  }, []);

  const beginRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    let mime = "video/webm;codecs=vp8,opus";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "video/webm";
    if (!MediaRecorder.isTypeSupported(mime)) mime = "video/mp4";
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime.split(";")[0] });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setStatus("done");
      stopStream();
      onRecorded(blob, posterRef.current);
    };
    recorder.start();
    posterRef.current = null;
    posterTimeoutRef.current = setTimeout(captureFrame, 1000);
    setSeconds(0);
    setStatus("recording");
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stopRecording();
        return s + 1;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    const cd = Number.isFinite(countdownSeconds) ? countdownSeconds : 3;
    if (!cd || cd <= 0) {
      beginRecording();
      return;
    }
    clearInterval(countdownRef.current);
    setCountdown(cd);
    setStatus("countdown");
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          beginRecording();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    posterRef.current = null;
    onRecorded(null, null);
    setStatus("idle");
    startCamera();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-4">
      {error && (
        <div data-testid="camera-error" className="p-4 border border-red-200 bg-red-50 text-red-700 text-sm rounded-md">
          {error}
        </div>
      )}

      {status === "idle" && (
        <button
          data-testid="enable-camera-btn"
          onClick={startCamera}
          className="w-full py-8 border-2 border-dashed border-zinc-300 rounded-lg flex flex-col items-center gap-3 text-zinc-600 hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors"
        >
          <Video size={32} />
          <span className="font-semibold">Ativar câmera</span>
          <span className="text-xs text-zinc-400">O navegador vai pedir permissão de câmera e microfone</span>
        </button>
      )}

      {(status === "ready" || status === "countdown" || status === "recording") && (
        <div className="relative rounded-lg overflow-hidden bg-black aspect-[3/4] sm:aspect-video">
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          {status === "countdown" && (
            <div
              data-testid="video-countdown-overlay"
              className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-[2px]"
            >
              <div
                key={countdown}
                data-testid="video-countdown-number"
                className="text-white font-black tracking-tighter select-none animate-countdown-pulse"
                style={{ fontSize: "clamp(6rem, 22vw, 12rem)", lineHeight: 1, textShadow: "0 6px 40px rgba(0,0,0,0.5)" }}
              >
                {countdown}
              </div>
            </div>
          )}
          {status === "recording" && (
            <>
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
                <span className="rec-dot w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="text-white text-xs font-semibold" data-testid="rec-timer">{fmt(seconds)}</span>
              </div>
              {questions.length > 0 && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-10">
                  <p className="text-white/60 text-[10px] uppercase tracking-widest mb-1">Perguntas para te guiar</p>
                  <ul className="space-y-0.5">
                    {questions.map((q, i) => (
                      <li key={i} className="text-white text-xs sm:text-sm">• {q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {status === "done" && previewUrl && (
        <div className="rounded-lg overflow-hidden bg-black aspect-[3/4] sm:aspect-video">
          <video src={previewUrl} controls playsInline className="w-full h-full object-contain" data-testid="video-preview" />
        </div>
      )}

      <div className="flex gap-3">
        {status === "ready" && (
          <button
            data-testid="start-recording-btn"
            onClick={startRecording}
            className="flex-1 py-3.5 bg-[#FF4F00] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#E64600] transition-colors"
          >
            <span className="w-3 h-3 rounded-full bg-white" /> Gravar
          </button>
        )}
        {status === "countdown" && (
          <button
            data-testid="countdown-recording-btn"
            disabled
            className="flex-1 py-3.5 bg-[#FF4F00]/70 text-white rounded-full font-semibold flex items-center justify-center gap-2 cursor-wait"
          >
            <span className="w-3 h-3 rounded-full bg-white animate-pulse" /> Começando em {countdown}...
          </button>
        )}
        {status === "recording" && (
          <button
            data-testid="stop-recording-btn"
            onClick={stopRecording}
            className="flex-1 py-3.5 bg-[#0A0A0A] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors"
          >
            <Square size={16} className="fill-white" /> Parar gravação
          </button>
        )}
        {status === "done" && (
          <button
            data-testid="rerecord-btn"
            onClick={reset}
            className="flex-1 py-3 border border-zinc-300 rounded-full font-semibold flex items-center justify-center gap-2 text-zinc-700 hover:border-zinc-500 transition-colors"
          >
            <RotateCcw size={16} /> Regravar
          </button>
        )}
        {status === "done" && (
          <div className="flex-1 py-3 rounded-full font-semibold flex items-center justify-center gap-2 text-green-700 bg-green-50 border border-green-200">
            <Check size={16} /> Vídeo pronto
          </div>
        )}
      </div>
    </div>
  );
};
